import { useState } from 'react';
import { useQuery } from 'react-query';
import { Search } from 'lucide-react';
import { apiClient } from '@/lib/api-client';

export default function AdminAuditLogPage() {
  const [page, setPage]   = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery(
    ['admin-audit-log', search, page],
    () => apiClient.get<any>('/admin/audit-log', { params: { search, page, limit: 50 } }),
    { keepPreviousData: true }
  );

  const entries: any[] = data?.entries ?? [];
  const total: number  = data?.total ?? 0;

  const actionColor = (action: string) => {
    if (action.startsWith('ban'))       return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    if (action.startsWith('warn'))      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
    if (action.startsWith('suspend'))   return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300';
    if (action.startsWith('unban') || action.startsWith('unsuspend')) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    if (action.includes('hide') || action.includes('freeze')) return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    return 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400';
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-1">Audit Log</h1>
      <p className="text-sm text-gray-500 dark:text-zinc-500 mb-6">All admin actions</p>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Filter by action or admin…"
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400" />
      </div>

      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-2">{[...Array(10)].map((_,i) => <div key={i} className="h-10 rounded bg-gray-100 dark:bg-zinc-800 animate-pulse" />)}</div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center text-gray-400 dark:text-zinc-600">No audit entries yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-zinc-800 text-xs text-gray-500 dark:text-zinc-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">When</th>
                <th className="px-4 py-3 text-left">Admin</th>
                <th className="px-4 py-3 text-left">Action</th>
                <th className="px-4 py-3 text-left">Target</th>
                <th className="px-4 py-3 text-left">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
              {entries.map(e => (
                <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors">
                  <td className="px-4 py-3 text-xs text-gray-400 dark:text-zinc-600 whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-medium">{e.admin_username}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionColor(e.action)}`}>{e.action}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-zinc-400">
                    {e.target_type && <span className="capitalize">{e.target_type}</span>}
                    {e.target_id && <span className="text-xs text-gray-400 dark:text-zinc-600 ml-1">#{e.target_id.slice(0,8)}</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-zinc-500 max-w-xs truncate">
                    {e.details ? JSON.stringify(e.details) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-zinc-800 text-sm text-gray-500 dark:text-zinc-500">
          <span>{total} total</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded border border-gray-200 dark:border-zinc-700 disabled:opacity-40">Prev</button>
            <span className="px-3 py-1">Page {page}</span>
            <button disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded border border-gray-200 dark:border-zinc-700 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
